import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase';

/**
 * Servicio para gestionar lotes de perfiles vendidos a concesionarias
 */
class LotesConcesionariaService {
  
  /**
   * Crear lote de perfiles vendido por agente a concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @param {string} agenteId - ID del agente vendedor
   * @param {number} cantidad - Cantidad de perfiles en el lote
   * @param {Object} datosAdicionales - Datos adicionales del lote
   * @returns {Promise<string>} ID del lote creado
   */
  async crearLote(concesionariaId, agenteId, cantidad, datosAdicionales = {}) {
    try {
      // Obtener datos de la concesionaria (buscar en empresas primero, luego en concesionarias)
      let concesionariaData = null;
      
      // Intentar buscar en empresas
      try {
        const empresaRef = doc(db, 'empresas', concesionariaId);
        const empresaSnap = await getDoc(empresaRef);
        if (empresaSnap.exists()) {
          concesionariaData = empresaSnap.data();
        }
      } catch (err) {
        console.log('No encontrado en empresas:', err);
      }
      
      // Si no se encontró en empresas, buscar en concesionarias
      if (!concesionariaData) {
        const concesionariaRef = doc(db, 'concesionarias', concesionariaId);
        const concesionariaSnap = await getDoc(concesionariaRef);
        if (concesionariaSnap.exists()) {
          concesionariaData = concesionariaSnap.data();
        }
      }
      
      if (!concesionariaData) {
        throw new Error('Concesionaria no encontrada');
      }
      
      // Obtener datos del agente (si no es admin_sistema)
      let agenteData = null;
      let agenteNombre = 'Admin Sistema';
      
      if (agenteId && agenteId !== 'admin_sistema') {
        try {
          const agenteRef = doc(db, 'agentes', agenteId);
          const agenteSnap = await getDoc(agenteRef);
          if (agenteSnap.exists()) {
            agenteData = agenteSnap.data();
            agenteNombre = agenteData.nombre || 'Sin nombre';
          }
        } catch (err) {
          console.log('Agente no encontrado, usando admin:', err);
        }
      }
      
      const loteData = {
        concesionariaId,
        concesionariaNombre: concesionariaData.nombre || concesionariaData.nombre_empresa || 'Sin nombre',
        agenteId: agenteId || 'admin_sistema',
        agenteNombre: agenteNombre,
        cantidad,
        cantidadUsada: 0,
        cantidadDisponible: cantidad,
        estado: 'activo',
        fechaCreacion: serverTimestamp(),
        fechaVenta: datosAdicionales.fechaVenta || serverTimestamp(),
        precioTotal: datosAdicionales.precioTotal || null,
        metodoPago: datosAdicionales.metodoPago || null,
        notas: datosAdicionales.notas || null,
        activo: true,
        creadoPor: agenteId === 'admin_sistema' ? 'admin' : 'agente'
      };
      
      const loteRef = await addDoc(collection(db, 'lotes_perfiles'), loteData);
      
      // Actualizar contadores en concesionaria (buscar en ambas colecciones)
      await this.actualizarContadoresConcesionaria(concesionariaId);
      
      console.log('✅ Lote creado:', loteRef.id);
      return loteRef.id;
      
    } catch (error) {
      console.error('Error creando lote:', error);
      throw error;
    }
  }
  
  /**
   * Actualizar contadores de perfiles en concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<void>}
   */
  async actualizarContadoresConcesionaria(concesionariaId) {
    try {
      // Obtener todos los lotes activos
      const lotesQuery = query(
        collection(db, 'lotes_perfiles'),
        where('concesionariaId', '==', concesionariaId),
        where('activo', '==', true)
      );
      const lotesSnap = await getDocs(lotesQuery);
      
      let totalComprados = 0;
      let totalUsados = 0;
      
      lotesSnap.forEach(doc => {
        const lote = doc.data();
        totalComprados += lote.cantidad || 0;
        totalUsados += lote.cantidadUsada || 0;
      });
      
      const contadores = {
        totalPerfilesComprados: totalComprados,
        totalPerfilesUsados: totalUsados,
        totalPerfilesDisponibles: totalComprados - totalUsados,
        fechaActualizacion: serverTimestamp()
      };
      
      // Actualizar en empresas (si existe)
      try {
        const empresaRef = doc(db, 'empresas', concesionariaId);
        const empresaSnap = await getDoc(empresaRef);
        if (empresaSnap.exists()) {
          await updateDoc(empresaRef, contadores);
        }
      } catch (err) {
        console.log('No encontrado en empresas:', err);
      }
      
      // Actualizar en concesionarias (si existe)
      try {
        const concesionariaRef = doc(db, 'concesionarias', concesionariaId);
        const concesionariaSnap = await getDoc(concesionariaRef);
        if (concesionariaSnap.exists()) {
          await updateDoc(concesionariaRef, contadores);
        }
      } catch (err) {
        console.log('No encontrado en concesionarias:', err);
      }
      
    } catch (error) {
      console.error('Error actualizando contadores:', error);
    }
  }
  
  /**
   * Incrementar contador de perfiles usados cuando se crea un perfil
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<void>}
   */
  async incrementarPerfilesUsados(concesionariaId) {
    try {
      // Obtener el primer lote activo con perfiles disponibles
      const lotesQuery = query(
        collection(db, 'lotes_perfiles'),
        where('concesionariaId', '==', concesionariaId),
        where('activo', '==', true)
      );
      const lotesSnap = await getDocs(lotesQuery);
      
      // Ordenar por fecha de creación (más antiguo primero - FIFO)
      const lotes = lotesSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const fechaA = a.fechaCreacion?.toDate?.() || a.fechaCreacion || new Date(0);
          const fechaB = b.fechaCreacion?.toDate?.() || b.fechaCreacion || new Date(0);
          return fechaA - fechaB;
        });
      
      // Buscar un lote con perfiles disponibles
      for (const lote of lotes) {
        if (lote.cantidadDisponible > 0) {
          const loteRef = doc(db, 'lotes_perfiles', lote.id);
          await updateDoc(loteRef, {
            cantidadUsada: (lote.cantidadUsada || 0) + 1,
            cantidadDisponible: (lote.cantidadDisponible || 0) - 1,
            fechaActualizacion: serverTimestamp()
          });
          
          // Actualizar contadores globales
          await this.actualizarContadoresConcesionaria(concesionariaId);
          break;
        }
      }
      
    } catch (error) {
      console.error('Error incrementando perfiles usados:', error);
    }
  }
  
  /**
   * Obtener lotes de una concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<Array>} Lista de lotes
   */
  async obtenerLotes(concesionariaId) {
    try {
      const lotesQuery = query(
        collection(db, 'lotes_perfiles'),
        where('concesionariaId', '==', concesionariaId),
        where('activo', '==', true)
      );
      const lotesSnap = await getDocs(lotesQuery);
      
      return lotesSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => {
          const fechaA = a.fechaCreacion?.toDate?.() || a.fechaCreacion || new Date(0);
          const fechaB = b.fechaCreacion?.toDate?.() || b.fechaCreacion || new Date(0);
          return fechaB - fechaA; // Más recientes primero
        });
    } catch (error) {
      console.error('Error obteniendo lotes:', error);
      return [];
    }
  }
  
  /**
   * Obtener lotes vendidos por un agente
   * @param {string} agenteId - ID del agente
   * @returns {Promise<Array>} Lista de lotes
   */
  async obtenerLotesPorAgente(agenteId) {
    try {
      const lotesQuery = query(
        collection(db, 'lotes_perfiles'),
        where('agenteId', '==', agenteId),
        where('activo', '==', true)
      );
      const lotesSnap = await getDocs(lotesQuery);
      
      return lotesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error obteniendo lotes por agente:', error);
      return [];
    }
  }
  
  /**
   * Calcular comisiones futuras de un agente por lotes vendidos
   * @param {string} agenteId - ID del agente
   * @param {number} porcentajeComision - Porcentaje de comisión (default 10%)
   * @returns {Promise<Object>} { totalLotes, totalPerfilesVendidos, comisionEstimada }
   */
  async calcularComisionesFuturas(agenteId, porcentajeComision = 10) {
    try {
      const lotes = await this.obtenerLotesPorAgente(agenteId);
      
      const totalLotes = lotes.length;
      const totalPerfilesVendidos = lotes.reduce((sum, lote) => sum + (lote.cantidad || 0), 0);
      
      // Calcular comisión estimada (ejemplo: $1,000 CLP por perfil vendido)
      const precioPorPerfil = 1000; // Esto debería venir de configuración
      const valorTotal = totalPerfilesVendidos * precioPorPerfil;
      const comisionEstimada = valorTotal * (porcentajeComision / 100);
      
      return {
        totalLotes,
        totalPerfilesVendidos,
        valorTotal,
        comisionEstimada,
        porcentajeComision
      };
    } catch (error) {
      console.error('Error calculando comisiones futuras:', error);
      return {
        totalLotes: 0,
        totalPerfilesVendidos: 0,
        valorTotal: 0,
        comisionEstimada: 0,
        porcentajeComision
      };
    }
  }
}

export default new LotesConcesionariaService();


